<template>
    <div>
        <form>
            <div class="fields">
                <CableConfiguratorSelect
                    placeholder="Select"
                    label="Cross Sectional Area"
                    name="size"
                    :options="csaOptions"
                    :value.sync="cable.size"
                />

                <CableConfiguratorInput
                    label="Cable Length"
                    name="length"
                    type="number"
                    unit="m"
                    :value.sync="cable.length"
                />

                <CableConfiguratorSelect
                    :class="{'field--full': !isPreExistingSystem}"
                    label="Cable Phase/s"
                    name="phases"
                    :options="cableOptions"
                    :value.sync="cable.phases"
                />

                <CableConfiguratorSelect
                    v-if="limitationDevice"
                    class="uppercase"
                    placeholder="Select"
                    label="Export Limitation Device Location"
                    name="exportLimitation"
                    :options="eldlOptions"
                    :value.sync="limitationDevice.location"
                    @selected="exportLimitationSelected"
                />

                <CableConfiguratorInput
                    v-if="limitationDevice"
                    label="Limited to (w)"
                    name="limit"
                    type="number"
                    :value.sync="limitationDevice.limit"
                />

                <CableConfiguratorInput
                    v-if="isPreExistingSystem"
                    label="Inverter VA"
                    name="powerOutput"
                    type="number"
                    :value.sync="cable.powerOutput"
                />
            </div>
        </form>
    </div>
</template>
<script>
import { EventBus } from '../../../event-bus';
import CableConfiguratorInput from '@/jobs/calculators/voltage-rise/CableConfiguratorInput';
import CableConfiguratorSelect from '@/jobs/calculators/voltage-rise/CableConfiguratorSelect';

export default {
    components: {
        CableConfiguratorInput,
        CableConfiguratorSelect,
    },
    props: {
        valid: {
            type: Boolean,
            required: true,
        },
        previousCablePhase: {
            required: true,
        },
        inverterPhase: {
            required: true,
        },
        sourceCable: {
            required: true,
        },
        isPreExistingSystem: {
            type: Boolean,
            required: true,
        }
    },
    data() {
        return {
            cable: {},
            limitationDevice: null,
            csaOptions: [
                {
                    label: '1mm',
                    value: 1,
                },
                {
                    label: '1.5mm',
                    value: 1.5,
                },
                {
                    label: '2.5mm',
                    value: 2.5,
                },
                {
                    label: '4mm',
                    value: 4,
                },
                {
                    label: '6mm',
                    value: 6,
                },
                {
                    label: '10mm',
                    value: 10,
                },
                {
                    label: '16mm',
                    value: 16,
                },
                {
                    label: '25mm',
                    value: 25,
                },
                {
                    label: '35mm',
                    value: 35,
                },
                {
                    label: '50mm',
                    value: 50,
                },
                {
                    label: '70mm',
                    value: 70,
                },
                {
                    label: '95mm',
                    value: 95,
                },
            ],
            cableDistributionPhasesOptions: [
                {
                    label: 'Single - L1(RED)',
                    value: '0',
                    phase: 1,
                },
                {
                    label: 'Single - L2(WHITE)',
                    value: '1',
                    phase: 1,
                },
                {
                    label: 'Single - L3(BLUE)',
                    value: '2',
                    phase: 1,
                },
                {
                    label: 'Two phase - L1, L2 (RED, WHITE)',
                    value: '0,1',
                    phase: 2,
                },
                {
                    label: 'Two phase - L2, L3 (WHITE, BLUE)',
                    value: '1,2',
                    phase: 2,
                },
                {
                    label: 'Two phase - L3, L1 (BLUE, RED)',
                    value: '0,2',
                    phase: 2,
                },
                {
                    label: 'Three Phase - L1, L2, L3 (RED, WHITE, BLUE)',
                    value: '0,1,2',
                    phase: 3,
                },
            ],
        };
    },
    computed: {
        fields() {
            return { ...this.cable };
        },

        cableOptions() {
            // This is the first supply
            if (!this.previousCablePhase) {
                return this.cableDistributionPhasesOptions.filter(cableOption => {
                    return cableOption.phase <= this.cable.electricalInstallation;
                })
            }

            // This is the last supply to the inverters
            if (this.sourceCable) {
                // Check to see if the previous cable was three phase
                if (this.inverterPhase == 3) {
                    return this.cableDistributionPhasesOptions.filter(cableOption => {
                        let phases = this.previousCablePhase
                        return phases == cableOption.value && cableOption.phase == 3;
                    })
                }

                return this.cableDistributionPhasesOptions.filter(cableOption => {
                    let phases = this.previousCablePhase.split(',')
                    if (phases == 3) phases = ['0','1','2']
                    return phases.includes(cableOption.value);
                })
            }

            // Three phase options still available
            if (this.previousCablePhase == '0,1,2') {
                return this.cableDistributionPhasesOptions;
            }

            // Reduce options to previous phases selected
            return this.cableDistributionPhasesOptions.filter(cableOption => {
                const phases = this.previousCablePhase.split(',')
                return phases.includes(cableOption.value) || cableOption.value == this.previousCablePhase;
            })
        },

        eldlOptions() {
            if (this.$attrs.supplies) {
                const options = [];
                options.push({
                    label: 'Select',
                    value: null
                });
                this.$attrs.supplies.map(item => options.push({
                    label: item.label,
                    value: item.value || item.label
                }));

                return options;
            }

            return null;
        },
    },

    watch: {
        fields: {
            handler() {
                const fields = {...this.fields};
                const isValid = Object.values(fields).every(field => (field !== null && field !== ''));

                this.$emit('update:valid', isValid);
            },

            deep: true
        },
    },

    created() {
        const { cable, limitationdevice } = this.$attrs;
        this.cable = cable;

        if (limitationdevice) {
            this.limitationDevice = limitationdevice;
        }
    },

    methods: {
        exportLimitationSelected(value) {
            EventBus.$emit('show-warning', value);
        }
    },
}
</script>
